import React, { useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from '../../hooks/useFocusTrap';

/**
 * SlideOver — canonical right/left slide-over panel primitive.
 *
 * WHY THIS EXISTS:
 *   Renders to `document.body` via React Portal so the overlay and panel are
 *   immune to ancestor containing-block traps (transform / filter /
 *   backdrop-filter / will-change / contain / perspective). Modules that
 *   roll their own slide-over silently break the moment a parent adds
 *   `backdrop-blur-*` to a glass header — this primitive cannot break.
 *
 * USAGE:
 *   <SlideOver isOpen={open} onClose={close} title="Record detail" width="dense">
 *     <SlideOver.Body>...</SlideOver.Body>
 *     <SlideOver.Footer>...</SlideOver.Footer>
 *   </SlideOver>
 *
 * RTL:
 *   side="end" (default) — opens from page-end (right in LTR, left in RTL).
 *   side="start" — opens from page-start (left in LTR, right in RTL).
 *
 * DNA: §3.6a — width tokens, RTL-safe positioning, header bg-slate-50.
 */

const WIDTH_CLASSES = {
  // Standard detail (≤20 fields) — DNA canonical width
  standard:
    'w-full sm:w-[85vw] md:w-[40vw] md:min-w-[380px] md:max-w-[600px]',
  // Dense detail (20–40 fields, multi-section forms) — DNA canonical wide width
  dense:
    'w-full sm:w-[85vw] md:w-[48vw] md:min-w-[420px] md:max-w-[780px]',
  // Wide for split-pane content (e.g., diff/compare views)
  wide:
    'w-full sm:w-[90vw] md:w-[60vw] md:min-w-[480px] md:max-w-[960px]',
  // Very wide for canvas/diagram payloads
  xwide:
    'w-full sm:w-[95vw] md:w-[75vw] md:max-w-[1200px]',
};

const SlideOver = ({
  isOpen,
  open,
  onClose,
  title,
  subtitle,
  width = 'standard',
  side = 'end',
  ariaLabel,
  ariaLabelledBy,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  showHeader = true,
  disableClose = false,
  headerRight = null,
  className = '',
  panelClassName = '',
  panelId,
  backdropTestId,
  children,
}) => {
  // Use i18next directly rather than LanguageContext — primitive needs to
  // render in test fixtures and pre-auth surfaces that don't mount the
  // LanguageProvider. i18next is globally initialized via initReactI18next
  // and has no provider requirement.
  const { i18n } = useTranslation();
  const isRTL = i18n?.dir ? i18n.dir() === 'rtl' : false;
  const panelRef = useRef(null);
  const isVisible = isOpen ?? open ?? false;

  // WCAG 2.3.3 — honor `prefers-reduced-motion`. When set, collapse all
  // transition durations to 0 and drop the slide offset so the panel appears
  // instantly with no motion. Layout and the component API are unchanged.
  const prefersReducedMotion = useReducedMotion();

  useFocusTrap(panelRef, isVisible);

  useEffect(() => {
    if (!isVisible) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isVisible]);

  const handleEscape = useCallback(
    (e) => {
      if (!closeOnEscape || disableClose) return;
      if (e.key === 'Escape') onClose?.();
    },
    [closeOnEscape, disableClose, onClose],
  );

  useEffect(() => {
    if (!isVisible) return undefined;
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible, handleEscape]);

  const handleOverlayClick = () => {
    if (closeOnOverlayClick && !disableClose) onClose?.();
  };

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  if (!portalTarget) return null;

  const widthClass = WIDTH_CLASSES[width] || WIDTH_CLASSES.standard;

  // Side resolution:
  //   side="end"   → right in LTR, left in RTL
  //   side="start" → left in LTR, right in RTL
  // Slide-in direction matches the anchor side so the motion feels natural.
  const anchorRight = (side === 'end' && !isRTL) || (side === 'start' && isRTL);
  const anchorClass = anchorRight ? 'right-0' : 'left-0';
  // No horizontal slide when the user prefers reduced motion — the panel
  // simply appears at its resting position (x: 0).
  const slideFrom = prefersReducedMotion ? 0 : anchorRight ? '100%' : '-100%';

  const labelledBy = title ? 'pg-slideover-title' : ariaLabelledBy;

  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <div className={`fixed inset-0 z-[100] ${className}`} dir={isRTL ? 'rtl' : 'ltr'}>
          {/* Backdrop — strong dim + soft blur, immune to containing-block traps. */}
          <motion.div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.18 }}
            onClick={handleOverlayClick}
            aria-hidden="true"
            data-testid={backdropTestId}
          />

          {/* Panel */}
          <motion.div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-modal="true"
            aria-label={!title ? ariaLabel : undefined}
            aria-labelledby={labelledBy}
            dir={isRTL ? 'rtl' : 'ltr'}
            className={`fixed top-0 ${anchorClass} h-full ${widthClass} bg-white shadow-2xl flex flex-col overflow-hidden ${panelClassName}`}
            initial={{ x: slideFrom }}
            animate={{ x: 0 }}
            exit={{ x: slideFrom }}
            transition={{ type: 'tween', duration: prefersReducedMotion ? 0 : 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Default header — rendered only when caller provides `title` and
                hasn't opted out via `showHeader={false}`. For custom layouts
                (sticky multi-row breach header with stage indicator and
                countdown, etc.) omit `title` and put a <SlideOver.Header>
                inside `children`. */}
            {title && showHeader && (
              <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200 bg-slate-50">
                <div className="min-w-0 flex-1">
                  <h2
                    id="pg-slideover-title"
                    className="text-lg font-semibold text-slate-900 truncate"
                  >
                    {title}
                  </h2>
                  {subtitle && (
                    <p className="mt-0.5 text-sm text-slate-500 truncate">{subtitle}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {headerRight}
                  {showCloseButton && (
                    <button
                      type="button"
                      onClick={() => !disableClose && onClose?.()}
                      disabled={disableClose}
                      className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    portalTarget,
  );
};

/**
 * SlideOver.Header — composition slot for fully custom headers (sticky
 * multi-row layouts, stage indicators, countdowns, breadcrumbs). Use this
 * INSTEAD of the `title` / `subtitle` props when the simple two-line layout
 * isn't enough.
 *
 * Pass `onClose` if you want the built-in X button; otherwise render your own.
 */
SlideOver.Header = function SlideOverHeader({ className = '', children, onClose, showCloseButton = true }) {
  return (
    <div className={`flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200 bg-slate-50 ${className}`}>
      <div className="min-w-0 flex-1">{children}</div>
      {showCloseButton && onClose && (
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      )}
    </div>
  );
};

/** Scrollable body region — placed between header and footer. */
SlideOver.Body = function SlideOverBody({ className = '', children }) {
  return (
    <div className={`flex-1 overflow-y-auto px-5 py-5 space-y-4 ${className}`}>
      {children}
    </div>
  );
};

/** Footer with end-aligned actions. */
SlideOver.Footer = function SlideOverFooter({ className = '', children }) {
  return (
    <div
      className={`flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-white ${className}`}
    >
      {children}
    </div>
  );
};

export default SlideOver;
